/**
 * Histórico de comentários do ambiente da planilha — o MESMO em todas as
 * páginas do Legado e no Mapa da Produção.
 *
 * O comentário fica no sistema, nunca na planilha: é ligado à chave
 * "PEDIDO:AMBIENTE" (`identidadeAmbiente`), então continua no histórico
 * quando o ambiente muda de etapa/página. Renomeação na planilha se resolve
 * na tela de Conciliar do Mapa.
 *
 * Regras (Igor, 01/10/2026): quem vê a página comenta; só o autor edita ou
 * apaga, nos primeiros 15 minutos; cada comentário guarda a etapa do
 * ambiente e a página de onde veio. A OBSERVAÇÃO GERAL da planilha aparece
 * no topo, só leitura, separada dos comentários.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FiMessageSquare } from "react-icons/fi";

import AsyncButton from "../AsyncButton";
import { FLUXO_ETAPAS } from "../NavegacaoEtapa/etapas";
import { useAuth } from "../../context/AuthContext";
import { useDados } from "../../context/DadosContext";
import { useExportRender } from "../../context/ExportRenderContext";
import useContagemComentarios, {
  PERMS_HISTORICO_AMBIENTE,
  recarregarContagemComentarios,
} from "../../hooks/useContagemComentarios";
import { chaveAmbiente, rotuloAmbiente } from "../../pages/MapaProducao/dados/chaves";
import {
  apagarComentarioAmbiente,
  comentarAmbiente,
  editarComentarioAmbiente,
  listarComentariosAmbiente,
} from "../../services/mapaProducao";
import { getAmbienteCurrentStatus } from "../../utils/clientes";
import { hasValue } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { bloqueioDoComentario, indiceDeIdentidade } from "../../utils/identidadeAmbiente";
import {
  Aviso,
  Balao,
  Bloco,
  BotaoTexto,
  Cabecalho,
  Erro,
  Formulario,
  Item,
  LinhaDeAcoes,
  Lista,
  Observacao,
  Selo,
  Selos,
  Texto,
  Titulo,
  Vazio,
} from "./styles";

export const TAMANHO_COMENTARIO = 4000;
const RELOGIO_MS = 30_000;

const PAGINAS_FORA_DO_FLUXO = [
  { prefixo: "/legado/clientes", label: "Clientes" },
  { prefixo: "/legado/mapa", label: "Mapa da Produção" },
];

const FORMATO_DATA_HORA = new Intl.DateTimeFormat("pt-BR", {
  timeZone: "America/Sao_Paulo",
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

/** "01/10/2026 às 14:32" no fuso de São Paulo (ADR-0059). */
export function formatarMomento(iso) {
  const data = iso ? new Date(iso) : null;
  if (!data || Number.isNaN(data.getTime())) return "";
  return FORMATO_DATA_HORA.format(data).replace(", ", " às ");
}

/** Nome da página do Legado pela rota — o "via Medição" do comentário. */
export function paginaDaRota(pathname = "") {
  const etapa = FLUXO_ETAPAS.find((item) => item.to === pathname);
  if (etapa) return etapa.label;
  const fora = PAGINAS_FORA_DO_FLUXO.find((item) => pathname.startsWith(item.prefixo));
  return fora ? fora.label : "";
}

function minutosRestantes(editavelAte, agora) {
  const fim = new Date(editavelAte).getTime();
  return Math.max(0, Math.ceil((fim - agora) / 60_000));
}

function useComentarios(chave, ativo) {
  const { request } = useAuth() || {};
  const [lido, setLido] = useState({ chave: null, lista: [], erro: null, carregando: false });
  const [gatilho, setGatilho] = useState(0);

  useEffect(() => {
    if (!ativo || !chave) return undefined;
    let vivo = true;
    setLido((atual) => ({ ...atual, carregando: true }));
    listarComentariosAmbiente(request, chave)
      .then((lista) => {
        if (vivo) setLido({ chave, lista: Array.isArray(lista) ? lista : [], erro: null, carregando: false });
      })
      .catch((erro) => {
        if (!vivo) return;
        // Ao reler o MESMO ambiente mantém a lista à vista; outro ambiente nunca
        // herda os comentários do anterior.
        setLido((atual) => ({
          chave,
          lista: atual.chave === chave ? atual.lista : [],
          erro: erro?.message || "Não foi possível ler o histórico.",
          carregando: false,
        }));
      });
    return () => {
      vivo = false;
    };
  }, [request, chave, ativo, gatilho]);

  const doAmbiente = lido.chave === chave;
  return {
    lista: doAmbiente ? lido.lista : [],
    erro: doAmbiente ? lido.erro : null,
    carregando: !doAmbiente || lido.carregando,
    reler: () => setGatilho((n) => n + 1),
  };
}

function ItemComentario({ comentario, agora, bloqueado, onAlterado }) {
  const { request } = useAuth();
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(comentario.texto);
  const [confirmandoApagar, setConfirmandoApagar] = useState(false);
  const restantes = minutosRestantes(comentario.editavel_ate, agora);
  const podeAlterar = comentario.pode_alterar && restantes > 0 && !bloqueado;

  useEffect(() => {
    if (!podeAlterar) {
      setEditando(false);
      setConfirmandoApagar(false);
    }
  }, [podeAlterar]);

  return (
    <Item>
      <Cabecalho>
        <strong>{comentario.autor || "Alguém"}</strong>
        <time dateTime={comentario.criado_em}>{formatarMomento(comentario.criado_em)}</time>
        {comentario.editado_em && (
          <span title={`Editado em ${formatarMomento(comentario.editado_em)}`}>(editado)</span>
        )}
      </Cabecalho>
      {(comentario.etapa || comentario.pagina) && (
        <Selos>
          {comentario.etapa && <Selo title={`Etapa do ambiente no momento: ${comentario.etapa}`}>{comentario.etapa}</Selo>}
          {comentario.pagina && <Selo title="Página de onde o comentário foi feito">via {comentario.pagina}</Selo>}
        </Selos>
      )}
      {editando ? (
        <Formulario>
          <textarea
            value={rascunho}
            maxLength={TAMANHO_COMENTARIO}
            onChange={(e) => setRascunho(e.target.value)}
            aria-label="Editar comentário"
          />
          <LinhaDeAcoes>
            <span className="dica">{`Pode alterar por mais ${restantes} min.`}</span>
            <BotaoTexto type="button" onClick={() => setEditando(false)}>
              Cancelar
            </BotaoTexto>
            <AsyncButton
              variant="primary"
              disabled={!rascunho.trim() || rascunho.trim() === comentario.texto}
              successMessage="Comentário editado."
              onClick={async () => {
                await editarComentarioAmbiente(request, comentario.id, rascunho.trim());
                setEditando(false);
                onAlterado();
              }}
            >
              Salvar
            </AsyncButton>
          </LinhaDeAcoes>
        </Formulario>
      ) : (
        <Texto>{comentario.texto}</Texto>
      )}
      {podeAlterar && !editando && (
        <LinhaDeAcoes>
          <span className="dica">{`Você pode alterar por mais ${restantes} min.`}</span>
          {confirmandoApagar ? (
            <>
              <BotaoTexto type="button" onClick={() => setConfirmandoApagar(false)}>
                Manter
              </BotaoTexto>
              <AsyncButton
                variant="danger"
                successMessage="Comentário apagado."
                onClick={async () => {
                  await apagarComentarioAmbiente(request, comentario.id);
                  onAlterado();
                }}
              >
                Apagar mesmo
              </AsyncButton>
            </>
          ) : (
            <>
              <BotaoTexto
                type="button"
                onClick={() => {
                  setRascunho(comentario.texto);
                  setEditando(true);
                }}
              >
                Editar
              </BotaoTexto>
              <BotaoTexto type="button" $perigo onClick={() => setConfirmandoApagar(true)}>
                Apagar
              </BotaoTexto>
            </>
          )}
        </LinhaDeAcoes>
      )}
    </Item>
  );
}

/**
 * `row`: a linha CRUA da planilha (campos F.*). `pagina`: rótulo opcional da
 * página; sem ele vem da rota. `setorId`: o Mapa manda a sala do ambiente
 * para o comentário entrar no feed do setor. `onAlterado`: avisado depois de
 * comentar/editar/apagar (o Mapa relê a linha do tempo).
 */
export default function HistoricoAmbiente({ row, pagina, setorId, onAlterado }) {
  const { request, hasAnyPermission } = useAuth() || {};
  const { dados } = useDados() || {};
  const location = useLocation();
  const permitido = typeof hasAnyPermission === "function" && hasAnyPermission(PERMS_HISTORICO_AMBIENTE);

  const chave = chaveAmbiente(row);
  const indice = useMemo(() => (Array.isArray(dados) ? indiceDeIdentidade(dados) : null), [dados]);
  const bloqueio = bloqueioDoComentario(row, indice);
  const { lista, erro, carregando, reler } = useComentarios(chave, permitido);
  const [texto, setTexto] = useState("");
  const [agora, setAgora] = useState(() => Date.now());

  const temEditavel = lista.some((c) => c.pode_alterar);
  useEffect(() => {
    if (!temEditavel) return undefined;
    setAgora(Date.now());
    const id = setInterval(() => setAgora(Date.now()), RELOGIO_MS);
    return () => clearInterval(id);
  }, [temEditavel]);

  // Trocar de ambiente com o painel aberto não leva o rascunho junto.
  useEffect(() => {
    setTexto("");
  }, [chave]);

  if (!row || !permitido) return null;

  const observacao = hasValue(row[F.OBSERVACOES_GERAIS]) ? String(row[F.OBSERVACOES_GERAIS]).trim() : "";

  function depoisDeAlterar() {
    reler();
    recarregarContagemComentarios();
    if (onAlterado) onAlterado();
  }

  return (
    <Bloco aria-label="Histórico de comentários">
      <Titulo>
        <FiMessageSquare size={13} aria-hidden="true" /> Histórico
      </Titulo>
      {observacao && (
        <Observacao>
          <span>Observação da planilha</span>
          {observacao}
        </Observacao>
      )}
      {bloqueio ? (
        <Aviso role="note">{bloqueio.mensagem}</Aviso>
      ) : (
        <Formulario>
          <textarea
            value={texto}
            maxLength={TAMANHO_COMENTARIO}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Escreva um comentário sobre este ambiente…"
            aria-label="Novo comentário"
          />
          <LinhaDeAcoes>
            <span className="dica">Fica só no sistema, com seu nome, data e hora. A planilha não muda.</span>
            <AsyncButton
              variant="primary"
              disabled={!texto.trim()}
              successMessage="Comentário registrado."
              onClick={async () => {
                await comentarAmbiente(request, {
                  chave,
                  rotulo: rotuloAmbiente(row),
                  texto: texto.trim(),
                  etapa: getAmbienteCurrentStatus(row)?.status || "",
                  pagina: pagina || paginaDaRota(location.pathname),
                  ...(setorId ? { setor_id: setorId } : {}),
                });
                setTexto("");
                depoisDeAlterar();
              }}
            >
              Comentar
            </AsyncButton>
          </LinhaDeAcoes>
        </Formulario>
      )}
      {erro && <Erro role="alert">{erro}</Erro>}
      {chave && !erro && !carregando && lista.length === 0 && <Vazio>Nenhum comentário ainda.</Vazio>}
      {chave && carregando && lista.length === 0 && <Vazio>Carregando comentários…</Vazio>}
      {lista.length > 0 && (
        <Lista>
          {lista.map((comentario) => (
            <ItemComentario
              key={`${comentario.id}-${comentario.editado_em || ""}`}
              comentario={comentario}
              agora={agora}
              bloqueado={Boolean(bloqueio)}
              onAlterado={depoisDeAlterar}
            />
          ))}
        </Lista>
      )}
    </Bloco>
  );
}

/**
 * Balão com o número de comentários do ambiente, para a célula da lista.
 * Nada quando não há comentário, a linha não tem chave ou a tela está sendo
 * capturada para exportação (o balão não é dado da planilha).
 */
export function BalaoComentarios({ row, chave: chaveDada }) {
  const { renderAll } = useExportRender() || {};
  const totais = useContagemComentarios({ ativo: !renderAll });
  const chave = chaveDada || chaveAmbiente(row);
  const total = chave ? totais[chave] || 0 : 0;
  if (renderAll || !total) return null;
  const rotulo = total === 1 ? "1 comentário" : `${total} comentários`;
  return (
    <Balao title={rotulo} aria-label={rotulo}>
      <FiMessageSquare size={12} aria-hidden="true" />
      {total}
    </Balao>
  );
}
